import { animate, state, style, transition, trigger } from '@angular/animations'
import { NgFor, NgIf } from '@angular/common'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { LabelDirective } from '@progress/kendo-angular-label'
import { ToastrService } from 'ngx-toastr'
import { Subscription } from 'rxjs'
import { DataService } from '../../@theme/services/data.service'
import { InitUserService } from '../../@theme/services/init-user.service'
import { UserService } from '../../@theme/services/user.service'
import { Column } from '../../models/column.model'
import {
  UserPreferences,
  UserPreferencesDB,
} from '../../models/userPreferences.model'

@Component({
    selector: 'gd-user-preferences',
    templateUrl: './user-preferences.component.html',
    styleUrls: ['./user-preferences.component.scss'],
    animations: [
        trigger('simpleFadeAnimation', [
            // the "in" style determines the "resting" state of the element when it is visible.
            state('in', style({ opacity: 1 })),
            // fade in when created. this could also be written as transition('void => *')
            transition(':enter', [style({ opacity: 0 }), animate(600)]),
            // fade out when destroyed. this could also be written as transition('void => *')
            transition(':leave', animate(600, style({ opacity: 0 }))),
        ]),
    ],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        LabelDirective,
        NgFor,
        NgIf,
    ]
})
export class UserPreferencesComponent implements OnInit, OnDestroy {
   userPreferences: UserPreferences = {} as UserPreferences;
  userPreferencesForm!: FormGroup;
  pendingTransactionColumns: Column[];
  transactionColumnSortOptions: Column[];
  clientColumnSortOptions: Column[];
  transactionLinkStatusOptions: any; // type as needed
  subscriptions = new Subscription();

  constructor(
    private initUserService: InitUserService,
    private userService: UserService,
    private formBuilder: FormBuilder,
    private dataService: DataService,
    private toastr: ToastrService,
  ) {
    // Initialize columns with filters applied
    this.pendingTransactionColumns = this.filterColumns(this.dataService.pendingTransactionColumns);
    this.transactionColumnSortOptions = this.filterColumns(this.dataService.allTransactionColumns);
    this.clientColumnSortOptions = this.filterColumns(this.dataService.clientColumns);
    this.transactionLinkStatusOptions = this.dataService.transactionLinkStatusOptions;

    this.setUserPreferencesForm();
    this.getUserPreferences();
    this.subscriptions.add(
      this.initUserService.currentRole.subscribe((role) => {
        const roleName = role.split('|')[0].toUpperCase();
        if (roleName === 'REP') {
          this.pendingTransactionColumns = this.removeFields(this.pendingTransactionColumns, ['branchNumber', 'repName']);
          this.transactionColumnSortOptions = this.removeFields(this.transactionColumnSortOptions, ['branchNumber', 'repName']);
        } else if (roleName === 'BRANCH MANAGER') {
          this.pendingTransactionColumns = this.removeFields(this.pendingTransactionColumns, ['branchNumber']);
          this.transactionColumnSortOptions = this.removeFields(this.transactionColumnSortOptions, ['branchNumber']);
        }
      }),
    );
  }
private filterColumns(columns: Column[]): Column[] {
  return columns.filter(item => item.hidden === false);
}

private removeFields(columns: Column[], fields: string[]): Column[] {
  return columns.filter(item => !fields.includes(item.field));
}
  ngOnInit(): void {
    this.subscriptions.add(
      this.initUserService.userPreferences$.subscribe((data) => {
        if (!data) return;
        this.userPreferences = data;
        this.userPreferencesForm.patchValue(this.userPreferences);
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe()
  }

  getUserPreferences() {
    this.userService
      .getUserPreferences()
      .subscribe((data: UserPreferencesDB) => {
        this.userPreferences = new UserPreferences(data)
        this.userPreferencesForm.reset(this.userPreferences)
        this.initUserService.setUserPreferences(this.userPreferences)
      })
  }

  onSaveUserPreferences() {
    // put form values into userPreferences object without overwriting the userPreferences object
    const userPreferencesParam = Object.assign(
      this.userPreferences,
      this.userPreferencesForm.value,
    )
    const userPreferencesDB = new UserPreferencesDB(userPreferencesParam)
    this.userService.saveUserPreferences(userPreferencesDB).subscribe(() => {
      this.toastr.success('User preferences saved successfully', 'Success')
      // Each grid's stored view state always carries a populated sort, so
      // without these the view state keeps overriding the sort just saved.
      this.initUserService.setAccountSortFromPreferences(
        userPreferencesParam.clientSortField,
        userPreferencesParam.clientSortDir,
      )
      this.initUserService.setPendingSortFromPreferences(
        userPreferencesParam.pendingSortField,
        userPreferencesParam.pendingSortDir,
      )
      this.initUserService.setTransactionSortFromPreferences(
        userPreferencesParam.transactionSortField,
        userPreferencesParam.transactionSortDir,
      )
      this.getUserPreferences()
    })
  }

  onCancelUserPreferences() {
    this.userPreferencesForm.reset(this.userPreferences)
  }

  private setUserPreferencesForm() {
    this.userPreferencesForm = this.formBuilder.group({
      clientRows: ['', Validators.required],
      clientSortField: ['', Validators.required],
      clientSortDir: ['', Validators.required],
      customTransactionLinkDaysBack: [''],
      customTransactionLinkStatus: [''],
      highLightTransactionsPendingAlerts: [''],
      pendingRows: ['', Validators.required],
      pendingSortField: ['', Validators.required],
      pendingSortDir: ['', Validators.required],
      transactionRows: ['', Validators.required],
      transactionSortField: ['', Validators.required],
      transactionSortDir: ['', Validators.required],
    })
  }
}
